import { getJwtPermissions, useSession } from '@descope/react-sdk'

import { useOrgDetails } from '@/lib/auth/orgid.hook'
import { UrlParam, useUrlParams } from '@/lib/url/url-param.hook'

import { isDevelopment, isPreview, isStaging } from '@/utils/env-utils.ts'

import { useIsSupportUser } from './support-user'

export const ALL_PERMISSIONS = [
  'base.view',
  'base.manage',
  'settings.view',
  'settings.manage',
  'integrations.view',
  'integrations.manage',
  'control.manage',
  'business-owner.manage',
  'company.manage',
  'custom-field.view',
  'custom-field.manage',
  'irq.manage',
  'poc.manage',
  'SUPPORT',
  'intake.manage',
] as const

export type PermissionKey = (typeof ALL_PERMISSIONS)[number]

export const useUserPermissions = (): string[] => {
  const { sessionToken, isAuthenticated, isSessionLoading } = useSession()
  const { orgDetails } = useOrgDetails()

  if (!isAuthenticated || isSessionLoading) {
    return []
  }

  const userPermissions = getJwtPermissions(sessionToken, orgDetails?.orgId)
  return userPermissions
}

export const useHasPermissions = (permissions?: PermissionKey[]): boolean => {
  const { urlParams } = useUrlParams()
  let userPermissions = useUserPermissions()
  const isSupportUser = useIsSupportUser()

  // Because of the maximal size of requests / cookies - we cannot add permissions per tenant to the support user.
  // So if a user is a support user (generally, not in a tenant), we allow all permissions.
  if (isSupportUser) {
    return true
  }

  // If no permissions are specified, return true (no permission check needed)
  if (!permissions || permissions.length === 0) {
    return true
  }

  // If the environment is development, preview, or staging, and the view as role parameter is set,
  // then use the the url params as the effective permissions
  if ((isDevelopment || isPreview || isStaging) && urlParams[UrlParam.VIEW_AS_ROLE]) {
    userPermissions = urlParams[UrlParam.VIEW_AS_ROLE].split(',')
  }

  return permissions.every((permission) => userPermissions.includes(permission))
}
